import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { getErrorMessage, getFirstErrorMessage } from "@/lib/form-errors";
import { useUser } from "@/lib/user-context";
import { trpc } from "@/utils/trpc";
import Loader from "./loader";

export default function SignUpForm({
  onSwitchToSignIn,
  redirectPath,
}: {
  onSwitchToSignIn: () => void;
  redirectPath?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSessionLoading } = useUser();
  const systemRoleQueryOptions = trpc.getSystemRole.queryOptions();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateSystemRoleMutation = useMutation({
    ...trpc.updateSystemRole.mutationOptions(),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(
        systemRoleQueryOptions.queryKey,
        variables.systemRole
      );
      toast.success("Account type updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update system role:", error);
      toast.error("Failed to update account type");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: systemRoleQueryOptions.queryKey,
      });
    },
  });

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
      name: "",
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      await authClient.signUp.email(
        {
          email: value.email,
          password: value.password,
          name: value.name,
        },
        {
          onSuccess: async () => {
            setSubmitError(null);
            toast.success("Sign up successful");

            const fallbackPath = "/onboarding";
            const safeRedirectPath = redirectPath?.startsWith("/")
              ? redirectPath
              : null;
            const nextPath = safeRedirectPath ?? fallbackPath;

            // Update the user's system role
            try {
              await updateSystemRoleMutation.mutateAsync({
                systemRole: "site_manager",
              });
            } catch (error) {
              console.error("Failed to update system role:", error);
            } finally {
              navigate(nextPath, { replace: Boolean(safeRedirectPath) });
            }
          },
          onError: (error) => {
            const message = getErrorMessage(error, "Failed to create account");
            setSubmitError(message);
            toast.error(message);
          },
        }
      );
    },
    validators: {
      onSubmit: z.object({
        name: z.string().min(2, "Name must be at least 2 characters"),
        email: z.email("Invalid email address"),
        password: z.string().min(8, "Password must be at least 8 characters"),
      }),
    },
  });

  if (isSessionLoading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
        className="space-y-4"
      >
        <div>
          <form.Field name="name">
            {(field) => {
              const fieldError = getFirstErrorMessage(
                field.state.meta.errors as ReadonlyArray<unknown>
              );

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Full Name</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    aria-invalid={Boolean(fieldError)}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      if (submitError) {
                        setSubmitError(null);
                      }
                      field.handleChange(e.target.value);
                    }}
                  />
                  <FieldErrorMessage message={fieldError} />
                </div>
              );
            }}
          </form.Field>
        </div>

        <div>
          <form.Field name="email">
            {(field) => {
              const fieldError = getFirstErrorMessage(
                field.state.meta.errors as ReadonlyArray<unknown>
              );

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Email</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="email"
                    value={field.state.value}
                    aria-invalid={Boolean(fieldError)}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      if (submitError) {
                        setSubmitError(null);
                      }
                      field.handleChange(e.target.value);
                    }}
                  />
                  <FieldErrorMessage message={fieldError} />
                </div>
              );
            }}
          </form.Field>
        </div>

        <div>
          <form.Field name="password">
            {(field) => {
              const fieldError = getFirstErrorMessage(
                field.state.meta.errors as ReadonlyArray<unknown>
              );

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Password</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="password"
                    value={field.state.value}
                    aria-invalid={Boolean(fieldError)}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      if (submitError) {
                        setSubmitError(null);
                      }
                      field.handleChange(e.target.value);
                    }}
                  />
                  <FieldErrorMessage message={fieldError} />
                </div>
              );
            }}
          </form.Field>
        </div>

        <FormErrorAlert message={submitError} />

        <form.Subscribe>
          {(state) => (
            <Button
              type="submit"
              className="w-full"
              disabled={!state.canSubmit || state.isSubmitting}
            >
              {state.isSubmitting ? "Creating Account..." : "Create Account"}
            </Button>
          )}
        </form.Subscribe>
      </form>

      <div className="text-center pt-4 border-t border-border">
        <Button
          variant="link"
          onClick={onSwitchToSignIn}
          className="text-foreground hover:underline underline-offset-4"
        >
          Already have an account? Sign In
        </Button>
      </div>
    </div>
  );
}
